// Shared USD presentation for the Bunker Hill demo path (D26: money reads US$,
// the convention for metal prices even at an Australian site).

/** The one money glyph. Typed labels (`US$/dst`, `US$/pt/day`) build on it. */
export const CURRENCY_GLYPH = 'US$';

export function currencyGlyph(currency: string): string {
  return currency === 'USD' ? CURRENCY_GLYPH : currency;
}

/**
 * Every envelope on the demo path is USD. The shared formatters below print a
 * US$ glyph and never re-read the currency, so a second deck must fail loudly
 * here rather than render as dollars.
 */
export function assertUsd(currency: string): void {
  if (currency !== 'USD') {
    throw new Error(`money: the demo path prices in USD, received "${currency}"`);
  }
}

export function formatCurrency(value: number): string {
  const rounded = Math.round(value);
  return `${rounded < 0 ? '−' : ''}${CURRENCY_GLYPH}${Math.abs(rounded).toLocaleString('en-US')}`;
}

/**
 * The same figure without the glyph, for a table whose column header already
 * carries it. `formatCurrency` owns the rounding, the sign and the grouping, so the
 * bare cell can never drift from the figure the glyph would have printed.
 */
export function formatCurrencyBare(value: number): string {
  return formatCurrency(value).replace(CURRENCY_GLYPH, '');
}

export function formatCurrencyCompact(value: number): string {
  const rounded = Math.round(value);
  const sign = rounded < 0 ? '−' : '';
  const absolute = Math.abs(rounded);
  if (absolute < 1_000) return formatCurrency(value);
  if (absolute < 1_000_000) return `${sign}${CURRENCY_GLYPH}${Math.round(absolute / 1_000)}K`;
  return `${sign}${CURRENCY_GLYPH}${(absolute / 1_000_000).toFixed(1)}M`;
}

/** Cost-and-revenue convention: two decimals for millions, whole dollars below. */
export function formatCurrencyStandard(value: number): string {
  const sign = value < 0 ? '−' : '';
  const absolute = Math.abs(value);
  if (absolute >= 1_000_000) return `${sign}${CURRENCY_GLYPH}${(absolute / 1_000_000).toFixed(2)}M`;
  if (absolute >= 10_000) return `${sign}${CURRENCY_GLYPH}${Math.round(absolute / 1_000)}K`;
  return formatCurrency(value);
}

export function formatCurrencyExact(value: number): string {
  const sign = value < 0 ? '−' : '';
  return `${sign}${CURRENCY_GLYPH}${Math.abs(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
